/**
 * How much of a route runs on a big road.
 *
 * The driver's requirement: there should ALWAYS be an option that stays on big
 * roads - two lanes each way or better - for all or most of the journey, even
 * when it is not the quickest. In a loaded truck that is not a preference: it
 * is the difference between overtaking safely and four hours behind a tractor
 * on a road with no shoulder.
 *
 * What the data can and cannot tell us
 * ------------------------------------
 * Mapbox has no "prefer major roads" parameter, so this cannot be requested -
 * only recognised among candidates. That leaves the question of how to
 * recognise it, and the honest answer for Thailand is narrower than it first
 * appears. Checked against live responses in Isaan:
 *
 *  - `maxspeed` annotations are 100% "unknown" here. Useless.
 *  - `intersections[].lanes` is present on anywhere between 3% and 84% of the
 *    intersections of a route, and it describes TURN lanes at a junction - a
 *    two-lane road shows three at a junction with a right-turn pocket. It
 *    cannot be read as carriageway width.
 *  - `mapbox_streets_v8.class` is complete and consistent.
 *
 * So this measures ROAD CLASS, and the naming here says so. `trunk` and
 * `motorway` in Thailand are the numbered national highways, which are divided
 * carriageways almost without exception; `primary` is a provincial highway,
 * usually wide but not guaranteed; `secondary` and below routinely means one
 * lane each way despite sounding substantial. An earlier version of this file
 * counted `secondary` as dual carriageway and reported "100% dual" for every
 * candidate including the one that avoided every highway - a number that was
 * both useless for ranking and actively misleading to read.
 *
 * Two figures matter, not one:
 *  - the SHARE of the trip on big roads, which is what "most of the way" means;
 *  - the LONGEST continuous run off them, because 90% big with a 40 km village
 *    section is not the route being asked for, and an average hides exactly
 *    that.
 */

import type { Route, RouteStep } from '../core/types';

/**
 * Classes that are genuinely big roads here. `trunk` and `motorway` are the
 * national highway network; `primary` is the provincial one and is included
 * because excluding it would reject perfectly good four-lane routes.
 */
const BIG_CLASSES = new Set([
  'motorway',
  'motorway_link',
  'trunk',
  'trunk_link',
  'primary',
  'primary_link',
]);

/**
 * Genuine motorways only - the 120 km/h grade-separated roads.
 *
 * Kept separate from the rest of the big roads because they are a different
 * drive, not merely a faster one: no traffic lights, no U-turn gaps, nothing
 * pulling out of a soi. A route that is 60% motorway and a route that is 60%
 * trunk road are not comparable, and reporting them under one figure hid that.
 */
const MOTORWAY_CLASSES = new Set(['motorway', 'motorway_link']);

/** Dual carriageway: 2+ lanes each way, but an ordinary road with junctions. */
const DUAL_CLASSES = new Set(['trunk', 'trunk_link', 'primary', 'primary_link']);

export interface RoadWidthProfile {
  /** Share on dual carriageway or better, 0..1. */
  bigFraction: number;
  /** Share on grade-separated 120 km/h motorway, 0..1. */
  motorwayFraction: number;
  /** Share on dual carriageway that is NOT motorway, 0..1. */
  dualFraction: number;
  /** Metres on roads below dual carriageway. */
  smallMetres: number;
  /** Longest unbroken run on roads below dual carriageway, metres. */
  worstSmallRun: number;
}

/** At or above this share, the route counts as big roads "most of the way". */
export const MOSTLY_BIG = 0.8;
/**
 * A run off the big roads longer than this disqualifies a route however good
 * its average is - roughly fifteen minutes of no overtaking.
 */
export const MAX_SMALL_RUN_M = 20_000;

/** Short links between big roads are not a detour onto small roads. */
const IGNORE_LINK_M = 300;

/** Dominant Mapbox street class over a step. */
function stepClass(step: RouteStep): string {
  const tally = new Map<string, number>();
  for (const x of step.intersections ?? []) {
    const c = x.mapbox_streets_v8?.class;
    if (c) tally.set(c, (tally.get(c) ?? 0) + 1);
  }
  let best = '';
  let top = 0;
  for (const [c, n] of tally) {
    if (n > top) {
      top = n;
      best = c;
    }
  }
  return best;
}

export function roadWidthProfile(route: Route): RoadWidthProfile {
  const steps: RouteStep[] = route.legs.flatMap((l) => l.steps);
  const total = Math.max(1, route.distance);

  let big = 0;
  let motorway = 0;
  let dual = 0;
  let small = 0;
  let run = 0;
  let worstRun = 0;

  for (const step of steps) {
    const cls = stepClass(step);
    const d = step.distance;
    const isBig = BIG_CLASSES.has(cls);

    if (isBig) {
      big += d;
      if (MOTORWAY_CLASSES.has(cls)) motorway += d;
      else if (DUAL_CLASSES.has(cls)) dual += d;
      // Only a decent stretch of big road actually breaks a bad run; a 150 m
      // slip road between two village sections does not.
      if (d > IGNORE_LINK_M) run = 0;
    } else {
      small += d;
      run += d;
      if (run > worstRun) worstRun = run;
    }
  }

  return {
    bigFraction: big / total,
    motorwayFraction: motorway / total,
    dualFraction: dual / total,
    smallMetres: Math.round(small),
    worstSmallRun: Math.round(worstRun),
  };
}

/** Does this route genuinely stay on big roads? */
export function isMostlyBig(p: RoadWidthProfile): boolean {
  return p.bigFraction >= MOSTLY_BIG && p.worstSmallRun <= MAX_SMALL_RUN_M;
}

/**
 * Rank for "biggest roads". Higher is better.
 *
 * The share leads, but a long run off the big roads is penalised directly, so
 * a route cannot average its way to the top while hiding a bad section. The
 * national-highway share breaks ties towards the road that is actually divided
 * rather than merely classified as primary.
 */
export function widthScore(p: RoadWidthProfile): number {
  const runPenalty = Math.min(0.35, p.worstSmallRun / 100_000);
  // Motorway breaks ties hardest - it is the only tier with no lights and no
  // junctions - with plain dual carriageway a smaller bonus over the rest.
  return p.bigFraction + p.motorwayFraction * 0.2 + p.dualFraction * 0.08 - runPenalty;
}

/**
 * Index of the best big-road route in a candidate pool, or -1 when empty.
 *
 * Never returns "none suitable": the driver asked for the option to exist
 * always, so the biggest available roads win even when the result is mediocre.
 * The interface states the actual percentage rather than implying a guarantee.
 */
export function bestBigRoadIndex(profiles: RoadWidthProfile[]): number {
  let best = -1;
  let bestScore = -Infinity;
  for (let i = 0; i < profiles.length; i++) {
    const score = widthScore(profiles[i]);
    if (score > bestScore) {
      bestScore = score;
      best = i;
    }
  }
  return best;
}
